import { useStore } from '@tanstack/react-form'
import { useMutation } from '@tanstack/react-query'
import { LakehouseConnectionTypeEnum } from '@vendia/management-api-types'
import { FetchFilePreviewDocument } from '@vendia/control-plane-api'
import debug from 'debug'
import { useNavigate } from 'react-router'

import Card from '../../../components/containers/card'
import RadioGroupField from '../../../components/fields/radio-group.field'
import { ScrollableStepContent } from '../../../components/flows/scrollable-step-content'
import StepButtons from '../../../components/flows/step-buttons'
import { StepContentHeader } from '../../../components/flows/step-header'
import { StepWrapper } from '../../../components/flows/step-wrapper'
import { StepComponent } from '../../../components/flows/types'
import { processCsvFile } from '../../../utils/csv-parser'
import { isRequiredValidator } from '../../../utils/form/validation'
import useControlPlaneApi from 'src/utils/hooks/use-control-plane-api'
import { LakehouseFlowStepValues } from '../../../utils/lakehouse/types'
import { lakehouseFlowFieldValidator } from '../../../utils/lakehouse/validators'
import notify from '../../../utils/notify'
import { StepId } from '../config'

const logger = debug('app:selectS3Files')

export const StepSelectS3Files: StepComponent<LakehouseFlowStepValues> = ({ context }) => {
  const form = context.form
  const navigate = useNavigate()
  const client = useControlPlaneApi()

  const { source, uniName, nodeName } = useStore(form.store, (s) => s.values)
  const s3 = source?.s3

  const fetchFilePreviewMutation = useMutation({
    mutationFn: (fileKey: string) =>
      client.graphqlFetch({
        query: FetchFilePreviewDocument,
        variables: {
          input: {
            uniName: uniName!,
            nodeName: nodeName!,
            bucket: s3?.bucketName!,
            bucketRegion: s3?.bucketRegion!,
            key: fileKey,
            maxBytes: 5 * 1024 * 1024,
            connectionInput: {
              type: LakehouseConnectionTypeEnum.S3,
              s3Connection: {
                crossAccountRoleArn: s3?.crossAccountRoleArn!,
                ...(s3?.encryptionKey && { encryptionKey: s3.encryptionKey }),
              },
            },
          },
        },
      }),
    onError: (error) => {
      notify.error(`Error fetching file preview: ${error}`)
    },
    onSuccess: async (response) => {
      if (response.errors) {
        notify.error(`${response.errors[0].message}`)
        return
      }
      if (!response.fetchFilePreview) {
        notify.error('Error fetching file preview')
        return
      }

      // Convert the content string to a Blob to mimic file processing
      const content = response.fetchFilePreview.content
      const blob = new Blob([content], { type: 'text/csv' })
      const file = new File([blob], source.selectedFile!, { type: 'text/csv' })

      // Form the complete S3 fileName for csvConfig
      const s3FileName = `s3://${s3?.bucketName}/${source.selectedFile}`

      form.setFieldValue('csvFile', file)

      const result = await processCsvFile(file, form, s3FileName)
      if (result.success) {
        form.setFieldValue('csvConfig', {
          fileName: s3FileName,
        })
        context.goToStep(StepId.ConfigureCsv)
      }
    },
  })

  const handleFilePreview = () => {
    if (!source.selectedFile) return
    fetchFilePreviewMutation.mutate(source.selectedFile)
  }

  return (
    <StepWrapper>
      <StepContentHeader
        centered
        large
        title='Select CSV file'
        description='Choose the CSV file to import from your S3 bucket'
      />
      <ScrollableStepContent inset autoHeight>
        <Card
          padding='xl'
          className='mx-8 w-full max-w-4xl'
          childrenWrapperClassName='flex w-full flex-grow flex-col gap-4'
          title='Available CSV files'
        >
          <div className='flex flex-col gap-1'>
            <RadioGroupField
              name='source.selectedFile'
              options={(source?.availableFiles ?? []).map((file: string) => ({
                label: file,
                value: file,
              }))}
              validators={lakehouseFlowFieldValidator({
                stepId: StepId.SelectS3File,
                validator: isRequiredValidator(),
              })}
              form={form}
            />
          </div>
          {source?.availableFiles?.length === 0 && (
            <div>No CSV files found in the specified S3 location. Please check your bucket and prefix settings.</div>
          )}
        </Card>
      </ScrollableStepContent>
      <form.Subscribe selector={(state) => [state.canSubmit]}>
        {([canSubmit]) => (
          <StepButtons
            context={context}
            hasCancel={true}
            onCancel={() => navigate('../', { relative: 'path' })}
            nextBlocked={!source.selectedFile || !canSubmit}
            isSubmitting={fetchFilePreviewMutation.isPending}
            onNext={() => {
              if (!source.selectedFile) {
                notify.error('Please select a CSV file')
                return
              }
              handleFilePreview()
            }}
          />
        )}
      </form.Subscribe>
    </StepWrapper>
  )
}
